import { useMemo, useRef } from 'react';
import { Hash } from 'lucide-react';
import type { GridColumn } from '@/grid/types';
import { CellEditor } from '@/grid/CellEditor';
import { formatValue, isNumericType } from '@/lib/format';
import { cn } from '@/lib/cn';
import type { Row } from './types';

/**
 * Ana kayıt dikey alan ızgarası (Delphi Master_Tablo: TcxDBVerticalGrid).
 * Alanlar Database_Yonetim'e göre (başlık, gizle, giriş kilidi, maske, seçim listesi, numaratör) çizilir.
 */
export function MasterForm(props: {
  columns: GridColumn[];
  row: Row;
  readOnly?: boolean;
  onChange: (patch: Row) => void;
  /** düzenlemeye başlamadan önce (BEFORE_EDIT); false dönerse alan kilitlenir */
  onBeforeEdit?: (col: GridColumn) => Promise<boolean>;
  onNumarator?: (col: GridColumn) => void;
  compact?: boolean;
}) {
  const fields = useMemo(() => props.columns.filter((c) => !c.hidden), [props.columns]);
  const allowed = useRef(new Set<string>());
  return (
    <div className="grid gap-x-4 gap-y-2" style={{ gridTemplateColumns: `repeat(auto-fill, minmax(${props.compact ? 200 : 240}px, 1fr))` }}>
      {fields.map((c) => {
        const ro = props.readOnly || c.readonly || c.editor === 'readonly';
        return (
          <label key={c.key} className="flex min-w-0 flex-col gap-0.5">
            <span className="h-label truncate !text-[10.5px]">{c.caption}</span>
            <div className={cn('flex h-[var(--control-h)] items-stretch overflow-hidden rounded-[var(--r-input)] border', ro ? 'b-border bg-[var(--fill)]' : 'border-[var(--input-border,var(--border))] bg-[var(--input-bg,var(--panel))] focus-within:border-[var(--accent)] focus-within:shadow-[0_0_0_3px_var(--focus-ring)]')}
              onFocusCapture={async (e) => {
                if (ro || !props.onBeforeEdit || allowed.current.has(c.key)) return;
                const target = e.target as HTMLElement;
                const ok = await props.onBeforeEdit(c);
                if (ok) allowed.current.add(c.key);
                else target.blur();
              }}>
              <div className={cn('flex min-w-0 flex-1', ro ? 'items-center' : 'items-stretch [&>*]:w-full')}>
                {ro ? (
                  <div className={cn('w-full truncate px-2 text-[var(--text-2)]', isNumericType(c.sqlType) && 'num text-right')}>{c.display ? c.display(props.row) : formatValue(props.row[c.key], c.sqlType, c.mask)}</div>
                ) : <CellEditor col={c} row={props.row} onChange={props.onChange} />}
              </div>
              {!ro && props.onNumarator && c.field?.numerator && (
                <button type="button" className="border-l b-border px-2 muted hover:text-[var(--accent)]" title="Numaratör" onClick={() => props.onNumarator!(c)}><Hash size={13} /></button>
              )}
            </div>
          </label>
        );
      })}
    </div>
  );
}
